/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { DynamicFormProps } from '@/shared/components/DynamicForm/DynamicForm'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import { useMenuTypesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useMenuTypesQuery'
import { useDishSizesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishSizesQuery'
import { useDishCategoriesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishCategoriesQuery'
import { buildSyncFieldsValidationSchema } from '@/features/menu-management/helpers/buildSyncFieldsValidationSchema'
import {
  AutoTranslateButton,
  AutoTranslateFormEffect
} from '@/features/menu-management/helpers/AutoTranslateFormEffect'
import { useIngredientsColumnsConfigInDetailsFrom } from './useIngredientsColumnsConfigInDetailsFrom'
import { useIngredientsQuery } from '../../Ingredients/hooks/useIngredientsQuery'
import { useToppingsQuery } from '../../Toppings/hooks/useToppingsQuery'
import { useDishesQuery } from './useDishesQuery'
import { useDishPriceBySizeColumnsConfigInDetailsFrom } from './useDishPriceBySizeColumnsConfigInDetailsForm'
import { currencySymbol } from '@/shared/utils/price'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'

export const useDishFormConfig = (dishId?: string) => {
  const { t } = useTranslation()
  const { menuTypesOptions } = useMenuTypesQuery()
  const { dishSizesOptions } = useDishSizesQuery()
  const { dishCategoriesOptions } = useDishCategoriesQuery()
  const ingredientsColumns = useIngredientsColumnsConfigInDetailsFrom()
  const priceBySizesColumns = useDishPriceBySizeColumnsConfigInDetailsFrom()
  const { ingredientsList, dietsList, allergensList } = useIngredientsQuery()
  const { toppingsList } = useToppingsQuery()
  const { dishImage, deleteDishImage } = useDishesQuery(dishId)

  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || 'en'

  const { defaultLanguageOption, selectedLanguagesOptions } =
    useSelectedLanguagesQuery(currentLanguage)

  const mappedIngredientsList =
    ingredientsList?.map(ingredient => ({
      label: ingredient.translations?.find(
        translation => translation.languageCode === currentLanguage
      )?.name,
      value: ingredient.id
    })) || []

  const mappedToppingsList =
    toppingsList?.map(topping => ({
      label: topping.translations?.find(
        translation => translation.languageCode === currentLanguage
      )?.name,
      value: topping.id
    })) || []

  const mappedAllergenList =
    allergensList.data?.map(allergen => ({
      label: allergen.translations?.find(
        translation => translation.languageCode === currentLanguage
      )?.name,
      iconImage: allergen.iconUrl,
      value: allergen.id
    })) || []

  const tabsOptions = [
    ...(defaultLanguageOption ? [defaultLanguageOption] : []),
    ...selectedLanguagesOptions.filter(
      o => o.value !== defaultLanguageOption?.value
    )
  ]

  const dietTypesOptions =
    dietsList?.data?.map(diet => ({
      label: diet.translations?.find(
        translation => translation.languageCode === currentLanguage
      )?.name,
      value: diet.id
    })) || []

  const formConfig = {
    id: 'dish-detail-form',
    FormEffects: <AutoTranslateFormEffect />,
    // Rendered by the modal in its action row rather than beside the language
    // tabs: a translation helper belongs with confirm, not wedged into the
    // form's own controls.
    footerAction: (
      <AutoTranslateButton
        defaultLanguage={defaultLanguageOption?.value || 'en'}
        allLanguages={selectedLanguagesOptions.map(o => o.value as string)}
        syncFieldNames={['dishName', 'dishDescription']}
      />
    ),
    fields: [
      {
        type: 'step',
        name: 'basicInfo',
        label: t('DISH_DETAILS_MODAL.FIRST_STEP_LABEL'),
        fields: [
          {
            name: 'translationsDescription',
            label: t('DISH_DETAILS_MODAL.TRANSLATIONS.DESCRIPTION'),
            type: 'label'
          },
          {
            name: 'selectedLanguages',
            type: 'tabs',
            tabs: tabsOptions,
            isSyncFormField: true,
            hideClearIcon: true
          },
          {
            name: 'dishName',
            label: t('DETAILS_TILE.MENU.NAME.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.NAME.PLACEHOLDER'),
            type: 'text',
            focused: true,
            required: true,
            validation: buildSyncFieldsValidationSchema(
              selectedLanguagesOptions
            ),
            syncFormField: 'selectedLanguages'
          },
          {
            name: 'dishDescription',
            label: t('DETAILS_TILE.MENU.DESCRIPTION.LABEL'),
            placeholder: t('DETAILS_TILE.MENU.DESCRIPTION.PLACEHOLDER'),
            type: 'text-area',
            focused: true,
            required: true,
            validation: buildSyncFieldsValidationSchema(
              selectedLanguagesOptions
            ),
            syncFormField: 'selectedLanguages'
          }
        ]
      },
      {
        type: 'step',
        label: t('DISH_DETAILS_MODAL.SECOND_STEP_LABEL'),
        fields: [
          {
            type: 'input-group',
            inputs: [
              {
                name: 'dishImage',
                type: 'image',
                label: t('DETAILS_TILE.MENU.IMAGE.LABEL'),
                // Without a slot of its own the field lands in the shared
                // DEFAULT_KEY slot, so the picture cropped for one dish shows
                // up on the next dish opened.
                modalId: IMAGE_SLOTS_KEYS.DISH_IMAGE,
                imageQuery: dishImage,
                // Only a saved dish has an image on the server to delete; for a
                // new one, dropping the slot is the whole job.
                onImageDelete: dishId ? () => deleteDishImage() : undefined
              },
              {
                type: 'input-group',
                inputGroupDirection: 'column',
                inputs: [
                  {
                    name: 'dishSizesDescription',
                    type: 'label',
                    label: t('DISH_DETAILS_MODAL.DISH_SIZES_DESCRIPTION')
                  },
                  {
                    type: 'rating',
                    name: 'spiceLevel',
                    precision: 1,
                    max: 3,
                    icon: <i className="las la-pepper-hot" />,
                    label: t('DETAILS_TILE.MENU.SPICE_LEVEL.LABEL')
                  }
                ]
              }
            ]
          },
          {
            type: 'input-group',
            inputs: [
              {
                name: 'dietTypes',
                label: t('DETAILS_TILE.MENU.DIET_TYPES.LABEL'),
                type: 'select',
                placeholder: t('DETAILS_TILE.MENU.DIET_TYPES.PLACEHOLDER'),
                focused: true,
                options: dietTypesOptions
              },
              {
                name: 'dishPrice',
                label: t('DETAILS_TILE.MENU.PRICE.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.PRICE.PLACEHOLDER'),
                type: 'text',
                focused: true,
                required: true,
                endAdornment: currencySymbol(),
                validation: z
                  .preprocess(val => {
                    if (typeof val === 'string') {
                      // Comma is the decimal separator people type here
                      const cleaned = val
                        .replace(',', '.')
                        .replace(/[^0-9.-]+/g, '')
                      return cleaned === '' ? undefined : Number(cleaned)
                    }
                    if (typeof val === 'number') return val
                    return undefined
                  }, z.number().min(0.01).max(999999999))
                  .optional()
              }
            ]
          },
          {
            type: 'input-group',
            inputs: [
              {
                name: 'dishNumber',
                label: t('DETAILS_TILE.MENU.DISH_NUMBER.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.DISH_NUMBER.PLACEHOLDER'),
                type: 'text',
                focused: true,
                required: true,
                validation: z
                  .union([
                    z.string().transform(x => x.replace(/[^0-9.-]+/g, '')),
                    z.number()
                  ])
                  .pipe(z.coerce.number().min(0.0001).max(999999999))
              },
              {
                name: 'dishSizes',
                label: t('DETAILS_TILE.MENU.SIZE.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.SIZE.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: dishSizesOptions
              }
            ]
          },
          {
            type: 'input-group',
            inputs: [
              {
                name: 'dishCategories',
                label: t('DETAILS_TILE.MENU.CATEGORY.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.CATEGORY.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: dishCategoriesOptions
              },
              {
                name: 'dishToppings',
                label: t('DETAILS_TILE.MENU.TOPPINGS.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.TOPPINGS.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: mappedToppingsList
              }
            ]
          },
          {
            type: 'input-group',
            inputs: [
              {
                name: 'dishIngredients',
                label: t('DETAILS_TILE.MENU.INGREDIENTS.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.INGREDIENTS.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: mappedIngredientsList
              },
              {
                name: 'dishTax',
                label: t('DETAILS_TILE.MENU.TAX.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.TAX.PLACEHOLDER'),
                type: 'text',
                focused: true,
                validation: z
                  .preprocess(val => {
                    if (typeof val === 'string') {
                      const cleaned = val
                        .replace(',', '.')
                        .replace(/[^0-9.]+/g, '')
                      return cleaned === '' ? undefined : Number(cleaned)
                    }
                    if (typeof val === 'number') return val
                    return undefined
                  }, z.number().min(0).max(100))
                  .optional()
              }
            ]
          },
          {
            type: 'input-group',
            inputs: [
              {
                name: 'menuTypes',
                label: t('DETAILS_TILE.MENU.MENU_TYPE.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.MENU_TYPE.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: menuTypesOptions
              },
              {
                name: 'ingredientAllergens',
                label: t('DETAILS_TILE.MENU.ALLERGENS.LABEL'),
                placeholder: t('DETAILS_TILE.MENU.ALLERGENS.PLACEHOLDER'),
                type: 'multi-select',
                focused: true,
                options: mappedAllergenList || []
              }
            ]
          }
        ]
      },
      {
        type: 'step',
        label: t('DISH_DETAILS_MODAL.THIRD_STEP_LABEL'),
        referenceField: 'dishIngredients',
        visibilityLogic: 'visibleIfNotEmpty',
        fields: [
          {
            name: 'ingredientsByDishSizeId',
            label: 'Ingredients by dish size',
            type: 'table',
            columns: ingredientsColumns,
            tableKey: 'PRODUCTS_TABLE',
            referenceField: 'dishIngredients',
            visibilityLogic: 'visibleIfNotEmpty',
            dataMatrixFields: ['dishIngredients', 'dishSizes'],
            editable: true,
            columnVisibilityRules: [
              {
                columnField: 'dishSizes',
                dependsOn: 'dishSizes',
                hideWhenEmpty: true
              }
            ]
          }
        ]
      },
      {
        type: 'step',
        label: t('DISH_DETAILS_MODAL.FOURTH_STEP_LABEL'),
        referenceField: 'dishSizes',
        visibilityLogic: 'visibleIfNotEmpty',
        fields: [
          {
            name: 'dishPriceBySizeList',
            label: 'Ingredients by dish size',
            type: 'table',
            columns: priceBySizesColumns,
            tableKey: 'PRODUCTS_TABLE',
            referenceField: 'dishSizes',
            visibilityLogic: 'visibleIfNotEmpty',
            dataMatrixFields: ['dishSizes'],
            editable: true
          }
        ]
      }
    ]
  }
  return formConfig as DynamicFormProps
}
